import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { Alert, Button, Chip, Stack, Typography } from "@mui/material"
import HighlightedMDEditor from "@pages/main/scripts/prompts/sub_components/HighlightedMDEditor"
import { useMemo, useRef } from "react"
import { extractConditionNames, extractVariableNames } from "./runner"

interface PromptTabProps {
  systemPrompt: string
  onChange: (value: string) => void
  isDirty: boolean
}

function estimateTokens(text: string) {
  const asciiSegments: string[] = text.match(/[A-Za-z0-9_]+/g) ?? []
  const asciiTokens = asciiSegments.reduce(
    (total, segment) => total + Math.ceil(segment.length / 4),
    0
  )
  const chars = Array.from(text)
  const nonAscii = chars.filter((char) => char.charCodeAt(0) > 127).length
  const punctuation = chars.filter(
    (char) => /[^\sA-Za-z0-9_]/.test(char) && char.charCodeAt(0) <= 127
  ).length

  return Math.max(0, asciiTokens + nonAscii + Math.ceil(punctuation / 2))
}

export default function PromptTab(props: PromptTabProps) {
  const { systemPrompt, onChange, isDirty } = props
  // Whatever the editor held when the drawer mounted — the local undo anchor.
  const openedWith = useRef(systemPrompt)

  const stats = useMemo(
    () => ({
      chars: Array.from(systemPrompt).length,
      tokens: estimateTokens(systemPrompt),
      variables: extractVariableNames(systemPrompt),
      conditions: extractConditionNames(systemPrompt)
    }),
    [systemPrompt]
  )

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="caption" color="text.secondary">
          {stats.chars.toLocaleString()} karakter • ~
          {stats.tokens.toLocaleString()} token
        </Typography>
        {stats.variables.map((name) => (
          <Chip
            key={name}
            size="small"
            variant="outlined"
            label={`{{${name}}}`}
          />
        ))}
        {stats.conditions.map((name) => (
          <Chip key={name} size="small" color="info" label={`if_${name}`} />
        ))}
        <Stack flex={1} />
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          disabled={systemPrompt === openedWith.current}
          onClick={() => onChange(openedWith.current)}
        >
          Açılıştaki hâline dön
        </Button>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
        Buradaki değişiklikler sayfadaki editörle aynı taslağı düzenler; testler
        anında yeni metinle çalışır. Kalıcı olması için “Kaydet”.
        {isDirty ? " Şu an kaydedilmemiş değişiklik var." : ""}
      </Alert>

      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <HighlightedMDEditor
          height="100%"
          value={systemPrompt}
          onChange={onChange}
        />
      </Stack>
    </Stack>
  )
}
